<template>
  <div class="real-time-monitor">
    <!-- 连接状态提示 -->
    <el-alert 
      v-if="isReconnecting"
      :title="reconnectMessage" 
      type="warning" 
      :closable="false"
      show-icon
      class="connection-alert"
    />
    
    <div class="layout-container">
      <!-- 上方设备信息区域 -->
      <div class="top-panel" :class="{ 'collapsed': !showDeviceInfo }">
        <!-- 设备信息头部 -->
        <div class="device-info-header" @click="toggleDeviceInfo">
          <div class="header-left">
            <el-icon :class="{ 'rotate': !showDeviceInfo }">
              <ArrowDown />
            </el-icon>
            <span class="header-title">{{ $t('message.realTimeData.deviceInfo') }}</span>
          </div>
          <div class="header-right">
            <el-button 
              type="primary" 
              link 
              @click.stop="toggleDeviceInfo"
              :icon="showDeviceInfo ? Fold : Expand"
              :title="showDeviceInfo ? $t('message.realTimeData.collapse') : $t('message.realTimeData.expand')"
            >
              {{ showDeviceInfo ? $t('message.realTimeData.collapse') : $t('message.realTimeData.expand') }}
            </el-button>
          </div>
        </div>

        <!-- 设备信息内容区域 -->
        <el-collapse-transition>
          <div v-show="showDeviceInfo" class="device-info-content">
            <device-info-panel 
              :device-info="currentDeviceInfo"
              v-if="currentDeviceId"
            />
            <div class="device-info-container">
              <div class="device-header">
                <h2>{{ currentDeviceInfo.name }}</h2>                
              </div>

              <div class="device-details-grid">
                <div class="detail-item">
                  <span class="detail-label">{{ $t('message.realTimeData.hostOrganization') }}</span>
                  <span class="detail-value">{{ currentDeviceInfo.organization }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">{{ $t('message.realTimeData.deviceTemplate') }}</span>
                  <span class="detail-value">{{ currentDeviceInfo.template }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">{{ $t('message.realTimeData.gateway')}}</span>
                  <span class="detail-value">{{ currentDeviceInfo.gateway }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">{{ $t('message.realTimeData.deviceAddress') }}</span>
                  <span class="detail-value">{{ currentDeviceInfo.address }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">{{ $t('message.realTimeData.deviceDescribe') }}</span>
                  <span class="detail-value">{{ currentDeviceInfo.description }}</span>
                </div>
              </div>
            </div>
          </div>
        </el-collapse-transition>
      </div>

      <!-- 下方控制面板区域 -->
      <div class="bottom-panel" :class="{ 'expanded': !showDeviceInfo }">
        <div class="control-panel">
          <el-form :inline="true" class="filter-form">
            <el-form-item :label="$t('message.realTimeData.companyDeviceTree')">
                <custom-tree-select
                  v-model="selectedDeviceId"
                  :external-device-id="currentDeviceId"
                  :tree-data="deviceTreeData"
                  :placeholder="$t('message.realTimeData.selectDevice')"                   
                  :loading="loading"
                  @change="handleDeviceChange"
                  @node-click="handleNodeClick"
                  class="filter-control variable-selector"
                  style="width: 200px"
                />                
            </el-form-item>
            
            <el-form-item :label="$t('message.realTimeData.slaveList')">          
              <el-select 
                v-model="currentSlaveId" 
                :placeholder="$t('message.realTimeData.SelectSalve')"
                style="width: 200px"
                clearable
                @change="handleSlaveChange"
              >
                <el-option
                  v-for="item in slaveOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>            
            </el-form-item>
          
            <el-form-item :label="$t('message.realTimeData.name_group')">
              <el-select
                v-model="selectedGroupIds"
                :placeholder="$t('message.realTimeData.selectVariableGroup')"
                style="width: 200px"
                multiple
                collapse-tags
                collapse-tags-tooltip
                clearable            
                @change="handleGroupChange"
              >
                <el-option
                  v-for="group in VarGroupOptions"
                  :key="group.value"
                  :label="group.label"
                  :value="group.value"
                />
              </el-select>
            </el-form-item>             
            <el-form-item :label="$t('message.realTimeData.display_name')">
              <el-input 
                v-model="currentVarName" 
                :placeholder="$t('message.realTimeData.inputVarName')" 
                clearable 
                @change="handleNameInput" 
              />
            </el-form-item>    

            <el-form-item>
              <el-button 
                type="primary" 
                @click="handleQuery"
                :disabled="!currentDeviceId"
              >
                {{ $t('message.realTimeData.query') }}
              </el-button>
            </el-form-item>    
          </el-form>      
          
          <div class="data-table-container">
            <el-table
              ref="tableRef"
              :data="tableData"
              border
              style="width: 100%"
              :height="tableHeight"
              v-loading="isLoading"
              @sort-change="handleSortChange"
              :default-sort="{ prop: 'display_name', order: 'descending' }"
            >
              <!-- 动态列 -->
              <el-table-column
                v-if="isColumnVisible('varname')"
                prop="varName"
                :label="$t('message.realTimeData.varname')"              
                width="150"
                sortable
              />
              
              <el-table-column
                v-if="isColumnVisible('display_name')"
                prop="display_name"
                :label="$t('message.realTimeData.display_name')"              
                width="220"
                sortable
              />

              <el-table-column
                v-if="isColumnVisible('name_group')"
                prop="name_group"
                :label="$t('message.realTimeData.name_group')"              
                width="150"
                sortable
              />

              <el-table-column
                v-if="isColumnVisible('description')"
                prop="description"
                :label="$t('message.realTimeData.Variable_description')"              
                width="200"
                sortable
              />

              <el-table-column
                v-if="isColumnVisible('updateTime')"
                prop="updateTime" 
                :label="$t('message.realTimeData.updateTime')"              
                width="180"
                sortable
              >
                <template #default="{ row }">
                  {{ formatDate(row.updateTime) || "--" }}
                </template>
              </el-table-column>

              <el-table-column
                v-if="isColumnVisible('currentValue')"
                prop="currentValue" 
                :label="$t('message.realTimeData.currentValue')"    
                width="140"
                sortable
              >
                <template #default="{ row }">
                  {{ formatCurrentValAndUnitDate(row)}}
                </template>
              </el-table-column>

              <el-table-column
                v-if="isColumnVisible('setValue')"
                prop="setValue"
                :label="$t('message.realTimeData.setValue')"
                width="120"                            
              >
                <template #default="{row}">
                  <div class="set-value-cell">
                    <span v-if="![null, undefined, '--'].includes(row.setValue)">
                      {{ row.setValue }}
                    </span>
                    <span v-else>--</span>
                    <el-button 
                      v-if="row.rwTypeId === 1 || row.rwTypeId === 3"
                      type="text" 
                      size="small"
                      class="edit-btn"
                      @click.stop="openEditDialog(row)"
                      :icon="Edit"
                    />
                  </div>
                </template>
              </el-table-column>

              <el-table-column
                v-if="isColumnVisible('deviceName')"
                prop="deviceName"
                :label="$t('message.realTimeData.deviceName')"
                width="140"
                sortable
              />
              
              <el-table-column
                v-if="isColumnVisible('slaveName')"
                prop="slaveName"
                :label="$t('message.realTimeData.SlaveName')"              
                width="150"
                sortable
              />
              
              <el-table-column 
                v-if="isColumnVisible('actions')"
                :label="$t('message.realTimeData.actions')"    
                width="200"
                fixed="right"
              >
                <template #default="{row}">
                  <el-button 
                    type="text" 
                    size="small"
                    @click="handleActiveCollection(row)"
                  >
                    {{ $t('message.realTimeData.activeCollection') }}              
                  </el-button>
                
                  <el-button 
                    type="text" 
                    size="small"
                    @click="handleTrack(row)"
                  >
                    {{ $t('message.reaTimePlot.track') }}
                  </el-button>         
                </template>

                <template #header>
                  <div class="table-header-tools">
                    {{ $t('message.realTimeData.actions') }}                    
                    <!-- 锁定滚动按钮 -->
                    <el-button 
                      :type="isScrollLocked ? 'success' : 'warning'"
                      @click="toggleScrollLock"
                      size="small"
                      :icon="isScrollLocked ? Lock : Unlock"
                      circle
                      class="header-tool-btn"
                      :title="isScrollLocked ? $t('message.realTimeData.unlockScroll') : $t('message.realTimeData.lockScroll')"
                    />
                    
                    <!-- 列显示控制按钮 -->
                    <el-popover
                      placement="bottom-end"
                      :width="200"
                      trigger="click"
                    >
                      <template #reference>
                        <el-button 
                          :icon="Setting" 
                          size="small"
                          circle
                          class="header-tool-btn"
                          :title="$t('message.realTimeData.columnSettings')"
                        />
                      </template>
                      <div class="column-settings">
                        <div class="column-settings-title">
                          {{ $t('message.realTimeData.selectColumns') }}
                        </div>
                        <el-checkbox-group v-model="visibleColumns">
                          <el-checkbox 
                            v-for="column in columnOptions" 
                            :key="column.prop" 
                            :label="column.prop"
                          >
                            {{ column.label }}
                          </el-checkbox>
                        </el-checkbox-group>
                        <div class="column-settings-actions">
                          <el-button size="small" @click="resetColumnSettings">
                            {{ $t('message.common.reset') }}
                          </el-button>
                          <el-button size="small" type="primary" @click="saveColumnSettings">
                            {{ $t('message.common.confirm') }}
                          </el-button>
                        </div>
                      </div>
                    </el-popover>
                  </div>
                </template>
              </el-table-column>
            </el-table>
            <el-pagination
              v-if="showPagination"
              v-model:current-page="pagination.currentPage"
              v-model:page-size="pagination.pageSize"
              :total="pagination.total"
              :page-sizes="[5,10, 20, 30]"
              layout="total, sizes, prev, pager, next, jumper"
              @current-change="handlePageChange"
              @size-change="handleSizeChange"
              class="pagination"
            />
          </div>
            
          
        </div>
      </div>
    </div>

    <!-- Edit Dialog -->
       <el-dialog
        v-model="editDialogVisible"
        :title="$t('message.realTimeData.editDialogTitle') + '--' + editForm.display_name + '(' + editForm.var_name +')'"
        width="400px"
      >
        <div class="edit-dialog-content">
          <el-form :model="editForm" label-width="120px">
            <el-form-item :label="$t('message.ClientDeviceMonitorSystemMonitorSetup.currentValue')">              
              <span class="dialog-title">{{ editForm.current_value }} {{editForm.unit}}</span>              
            </el-form-item>            
            <el-form-item :label="$t('message.ClientDeviceMonitorSystemMonitorSetup.setupVlaue')">              
                   <el-input-number
                      v-model="editForm.setValue"
                      :min="editForm.minimum"
                      :max="editForm.maximum"
                      :step="editForm.step"
                      :precision="getPrecision(editForm.step)"
                      size="small"
                      @change="handleValueChange(editForm)"
                      controls-position="right"                      
                      class="set-value-input"
                    />
            </el-form-item>
            <el-form-item :label="$t('message.ClientDeviceMonitorSystemMonitorSetup.description')">                          
              <span class="dialog-title">{{ editForm.description }}</span>
            </el-form-item>
          </el-form>
          <div class="dialog-footer">
            <el-button @click="editDialogVisible = false">
              {{ $t("message.common.cancel") }}
            </el-button>
            <el-button type="primary" @click="confirmEdit">
              {{ $t("message.common.confirm") }}
            </el-button>
          </div>
        </div>
      </el-dialog>

    <!-- <el-dialog
      v-model="editDialogVisible"
      :title="$t('message.realTimeData.editDialogTitle')"
      width="25%"
    >  
        <el-form :model="editForm" label-width="120px">                
          <el-form-item :label="$t('message.realTimeData.setValue')">
            <el-input
              v-model="editForm.setValue"
              :placeholder="$t('message.realTimeData.valuePlaceholder')"
            />
          </el-form-item>
        </el-form>
          <template #footer>
          <span class="dialog-footer">
          <el-button @click="editDialogVisible = false">
            {{ $t('message.common.cancel') }}
          </el-button>
          <el-button type="primary" @click="confirmEdit">
            {{ $t('message.common.confirm') }}
          </el-button>
          </span>
        </template>

    </el-dialog> -->

    <!-- 指定间隔和指定时间追踪 -->
    <el-dialog
        v-model="TrackDialogVisible"
        :title="$t('message.reaTimePlot.track')"
        width="25%"
      >
        <el-form :model="trackForm" label-width="120px">                
          <el-form-item :label="$t('message.reaTimePlot.interval')">
            <el-input-number v-model="trackForm.intervalValue" :min="1" />
          </el-form-item>

          <el-form-item :label="$t('message.reaTimePlot.duration')">
            <el-input-number v-model="trackForm.durationValue" :min="1" />
          </el-form-item>
        </el-form>
        
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="TrackDialogVisible = false">
              {{ $t('message.common.cancel') }}
            </el-button>
            <el-button type="primary" @click="confirmTrack()">
              {{ $t('message.common.confirm') }}
            </el-button>
          </span>
        </template>
      </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount, onUnmounted, computed, nextTick, watch } from 'vue';
import { onBeforeRouteLeave,useRoute } from 'vue-router';
import { ElMessage } from 'element-plus';
import { useI18n } from 'vue-i18n';
import { getToken } from '@/utils/auth';
import { useSignalR } from '@/utils/signalR';
import { getConfig } from '@/utils/configTool';
import { getCompanyDeviceTree } from '@/api/CompanyManager'
//import baseConfig from '@/public/config.json'
import { slavedetailbydevice, vargroupdetailbydevice } from '@/api/TemplateManagement'
import { getRealtimeList, rollCall, getDevices } from '@/api/DeviceManagement'
import { Edit, Setting, Lock, Unlock, ArrowDown, Fold, Expand } from '@element-plus/icons-vue'
import { heart } from '@/api/UserManager'
import CustomTreeSelect from '@/components/views/CompanyDeviceTree.vue'

const { t } = useI18n();
//const url = `${baseConfig.apiBaseUrl}/subscriptionHub`;
const url = ref('');

// 折叠控制变量
const showDeviceInfo = ref(true); // 是否显示设备信息

// 表格高度计算 - 根据设备信息显示状态调整
const tableHeight = computed(() => {
  return showDeviceInfo.value ? 'calc(100vh - 380px)' : 'calc(100vh - 280px)';
});

// 切换设备信息显示/隐藏
const toggleDeviceInfo = () => {
  showDeviceInfo.value = !showDeviceInfo.value;
};

 
 
// 初始化 SignalR
const {
  isConnected,
  error,  
  isReconnecting,     // 暴露重连状态
  reconnectAttempts,  // 暴露重连尝试次数
  reconnectDelay,     // 暴露当前重连延迟时间
  start,
  stop,
  invoke,
  on,
  off,
  onConnected
} = useSignalR(url, {
  accessTokenFactory: () => getToken() || '',automaticReconnect: true, // 如果底层库支持
})

const route = useRoute();
// 设备树数据
const deviceTreeOptions = ref([]);
const currentDeviceId = ref(null);
const currentDeviceInfo = ref({
  name: '',
  deviceCode: '',
  organization: '',
  template: '',
  gateway: '',
  address: '',
  tags: '-',
  description: ''
});
const currentSlaveId =ref(null)

const selectedGroupIds = ref([]);
const selectedGroupNames = ref([]);

const slaveOptions = ref([]) // 定义 slaveOptions
const VarGroupOptions = ref([]) // 定义 变量分组
const currentVarName = ref();

// 树选择器实例
//const treeSelectRef = ref(null);

// 数据状态
const tableData = ref([]);
const cachedData = ref([]);
const isLoading = ref(false);
const chartInstance = ref(null);

// 添加滚动相关变量
const tableRef = ref(null);
const isLoadingMore = ref(false);
const hasMoreData = ref(true);
const showPagination = ref(true); // 可以选择隐藏分页，使用滚动加载
let scrollContainer = null;
let debounceTimer = null;

// 分页配置
const pagination = reactive({
  currentPage: 1,
  pageSize: 20,
  total: 0
});

// 定时器
const refreshTimer = ref(null);
const heartBeatTimer = ref(null);
const refreshInterval = 30000; // 30秒
const heartBeatInterval = 600000; // 10分钟
let trackTimer = null;

// Add these to your existing script
const TrackDialogVisible = ref(false);
const trackForm = ref({  
  intervalValue: 1,
  durationValue: 1
});


// 在现有的变量定义后面添加
const isScrollLocked = ref(true); // 锁定滚动状态

// 锁定滚动功能
const lockScroll = () => {
  isScrollLocked.value = true;
  ElMessage.success(t('message.realTimeData.scrollLocked'));
};

// 解锁滚动功能
const unlockScroll = () => {
  handleSizeChange(30);
  isScrollLocked.value = false;
  ElMessage.success(t('message.realTimeData.scrollUnlocked'));
};

// 切换锁定状态
const toggleScrollLock = () => {
  if (isScrollLocked.value) {
    unlockScroll();
  } else {
    lockScroll();
  }
};


// 列配置
const columnOptions = [  
  { prop: 'varname', label: t('message.realTimeData.varname') },
  { prop: 'display_name', label: t('message.realTimeData.display_name') },
  { prop: 'name_group', label: t('message.realTimeData.name_group') },
  { prop: 'description', label: t('message.realTimeData.Variable_description') },
  { prop: 'updateTime', label: t('message.realTimeData.updateTime') },
  { prop: 'currentValue', label: t('message.realTimeData.currentValue') },
  { prop: 'setValue', label: t('message.realTimeData.setValue') },
  { prop: 'deviceName', label: t('message.realTimeData.deviceName') },
  { prop: 'slaveName', label: t('message.realTimeData.SlaveName') },
  //{ prop: 'actions', label: t('message.realTimeData.actions') }  
];

// 默认显示的列
const defaultVisibleColumns = [
  'display_name', 'updateTime', 'currentValue', 'setValue', 'actions'
];

// 可见列配置
const visibleColumns = ref([...defaultVisibleColumns]);

// 检查列是否可见
const isColumnVisible = (columnProp) => {
  return visibleColumns.value.includes(columnProp);
};

// 保存列设置到本地存储
const saveColumnSettings = () => {
  localStorage.setItem('realTimeTableColumns', JSON.stringify(visibleColumns.value));
  ElMessage.success(t('message.realTimeData.columnSettingsSaved'));
};

// 重置列设置
const resetColumnSettings = () => {
  visibleColumns.value = [...defaultVisibleColumns];
  saveColumnSettings();
};


// 3. 异步加载配置函数
async function loadConfig() {
  try {
      // 获取完整配置对象
    const config = await getConfig();
    // 解构使用
  //  const { signalRUrl, apiBaseUrl } = config;
    //console.log('SignalR 地址：', signalRUrl);
    //console.log('接口基础地址：', apiBaseUrl);    
    // 4. 赋值 SignalR url
    url.value = config.signalRUrl;
    //console.log('SignalR 地址配置成功：', signalRUrl.value);
  } catch (error) {
    console.error('加载配置文件失败：', error); 
  }
}

// 从本地存储加载列设置
const loadColumnSettings = () => {
  const savedColumns = localStorage.getItem('realTimeTableColumns');
  if (savedColumns) {
    try {
      visibleColumns.value = JSON.parse(savedColumns);
    } catch (error) {
      console.error('加载列设置失败:', error);
      visibleColumns.value = [...defaultVisibleColumns];
    }
  }
};




//当前值与单位并列
const formatCurrentValAndUnitDate = (row) => {
  // 如果 currentValue 为空或无效，返回 '--'
  if (row.currentValue == null || row.currentValue === '' || row.currentValue === '--') {
    return '--';
  }
  
  // 尝试转换为数字
  const currentValue = Number(row.currentValue);
  const mult = Number(row.mult ?? 1);
  
  // 检查是否为有效数字
  if (isNaN(currentValue) || !isFinite(currentValue)) {
    return '--';
  }
   
  //console.error(`formatCurrentValAndUnitDate --mult:${mult}--var_decimal: ${row.var_decimal}`);
  // 格式化结果
  const formattedValue = formatValue( currentValue , mult,row.var_decimal);
  
  // 组合单位和值
  const unit = row.unit ? ` ${row.unit}` : '';
  return `${formattedValue}${unit}`.trim();
};


const formatValue = (value, mult, varDecimal) => {
  const result = (Number(value) || 0) * (Number(mult) || 1);
  
  if (!isFinite(result)) return '0';
  if (result === 0) return '0';
  
  // 确定小数位数
  const decimal = varDecimal !== undefined && varDecimal !== null 
    ? Math.min(Math.max(Number(varDecimal) || 0, 0), 10)
    : 2;
  
  const absResult = Math.abs(result);
  let str;
  
  // 智能判断是否需要科学计数法
  const needsScientific = 
    absResult >= 1e12 ||                   // 太大
    (absResult < 1e-5 && absResult > 0) || // 太小
    result.toString().includes('e');       // 本来就是科学计数法
  
  if (needsScientific) {
    // 使用科学计数法
    str = result.toExponential(decimal);
    
    // 去除科学计数法中不必要的0
    str = str.replace(/(\.\d*?)0+e/, '$1e');
    str = str.replace(/\.e/, 'e');
    
  } else {
    // 正常处理
    const multiplier = Math.pow(10, decimal);
    const rounded = Math.round(result * multiplier) / multiplier;
    str = decimal === 0 ? rounded.toString() : rounded.toFixed(decimal);
    
    // 去除末尾的0
    if (str.includes('.')) {
      str = str.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
    }
  }
  
  return str;
};
 

// const formatCurrentValAndUnitDate = (row) => {
//   // 使用模板字符串，更清晰
//   return `${(row.currentValue ?? 0) * (row.mult ?? 1) ?? '-'} ${row.unit ?? ''}`.trim();
// };

const formatDate = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);

  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })
    .format(date)
    .replace(/\//g, "-") // 将斜杠替换为横杠
    .replace(/(\d+):(\d+):(\d+)/, "$1:$2:$3"); // 保持时间格式
};

//打开追踪窗体，设置定时，定长追踪变量
const handleTrack = (row) => {
    // 验证变量选择
  if (cachedData.value.length <= 0) {
    ElMessage.error(t('message.realTimeData.selectVariable'));
    return;
  }
  currentEditRow.value = row;
  TrackDialogVisible.value = true;
};

//执行追踪
const confirmTrack = async () => {
  
  // 先停止可能存在的旧定时器
  if (trackTimer) {
    clearInterval(trackTimer);
    trackTimer = null;
  }

  // 验证连接状态
  if (!isConnected) {
    ElMessage.error(t('message.realTimeData.connectError'));
    return;
  }

  // 验证变量选择
  if (cachedData.value.length <= 0) {
    ElMessage.error(t('message.realTimeData.selectVariable'));
    return;
  }

  try {
    
    // 立即执行一次数据获取
    await trackCmdMsg(currentEditRow.value);

    // 启动心跳定时器
    trackTimer = setInterval(async () => {
      try {
        await trackCmdMsg(currentEditRow.value);
      } catch (error) {
        console.error('定时数据获取失败:', error);
        // 出错时自动停止定时器
        if (trackTimer) {
          clearInterval(trackTimer);
          trackTimer = null;
        }
        ElMessage.error(t('message.realTimeData.dataProcessError'));
      }
    }, trackForm.value.intervalValue * 1000);

    // 设置自动停止时间（分钟转毫秒）
    const durationMs = trackForm.value.durationValue * 60 * 1000;
    setTimeout(() => {
      if (trackTimer) {
        clearInterval(trackTimer);
        trackTimer = null;
        console.log('定时器已自动停止');
        ElMessage.success(t('message.reaTimePlot.trackingCompleted'));
      }
    }, durationMs);
    TrackDialogVisible.value = false;
    ElMessage.success(t('message.reaTimePlot.trackingStarted'));

  } catch (error) {
    console.error('启动实时跟踪失败:', error);
    // 确保出错时定时器被清理
    if (trackTimer) {
      clearInterval(trackTimer);
      trackTimer = null;
    }
    ElMessage.error(t('message.realTimeData.dataProcessError'));
  }
};


//追踪
const trackCmdMsg= async(row)=>
{
  
  if(!isConnected)
  {
    ElMessage.error(t('message.realTimeData.connectError'));
    return
  }  

  try {
    
        const token = getToken();         
          // 1. 构造请求数据
        const requestData = [
            {
                token: token,
                deviceCode: currentDeviceInfo.value.deviceCode,
                imei: undefined,
                type: 4,  // 1=定时数据
                dir: "Down",
                timer: new Date().toISOString(),
                priority: 1,
                r_data: [],
                w_data: []
            }
        ];         

        //2.追加点名数据        
        requestData[0].r_data.push({ name: row.varName });   
        //const json = JSON.stringify(requestData)
        //ElMessage.error(`${json} trackCmdMsg`);
        await rollCall(requestData);        
  }catch(error)
  {
     console.log(t('message.realTimeData.dataProcessError'));        
  }
}

const reconnectMessage = computed(() => {
  return t('message.realTimeData.reconnecting', {
    attempts: reconnectAttempts.value,
    seconds: Math.round(reconnectDelay.value / 1000)
  });
});

// 树选择器配置
/* const treeSelectProps = {
  value: 'id',    
  label: 'name',
  children: 'children',
  parentId:'parentId',
  expandTrigger: 'click', // 点击触发展开（关键配置）
  checkStrictly: true  // 父子节点选中状态不关联
}; */




// Add these to your existing script
const editDialogVisible = ref(false);
 

const editForm = ref({  
  display_name: '',
        value: 1,
     setValue: 1,
         unit: '',
         mult: 1,
         step: 1,
  description: ''   //描述
});


// 修改现有的 getPrecision 方法
const getPrecision = (step) => {
  if (!step) return 2;
  
  // 处理浮点数精度问题
  const stepStr = step.toString();
  const decimalIndex = stepStr.indexOf('.');
  
  if (decimalIndex === -1) return 0;
  
  // 限制最大精度为6位，避免过长的小数
  const precision = stepStr.length - decimalIndex - 1;
  return Math.min(precision, 2);
};

// 修改 handleValueChange 方法
const handleValueChange = (param) => {
  console.log(`参数 ${param.title} 值改变为:`, param.setValue);
  
    // 重新赋值触发响应式更新
  const newValue = Number(param.setValue);  
  updateParameterSetValue(param.deviceCode, param.varName, newValue);
}

// 直接更新 cachedData 中的 setValue
const updateParameterSetValue = (deviceCode, varName, newSetValue) => {
  if (!cachedData.value || !Array.isArray(cachedData.value)) return;
  
  // 直接遍历数组
  cachedData.value.forEach(item => {
    if (item.deviceCode === deviceCode && item.varName === varName) {
      // 更新 setValue
      item.setValue = newSetValue === null ? '--' : Number(newSetValue);
      
      // 添加修改标记（如果数据结构中有这个字段）
      if (item.userModified !== undefined) {
        item.userModified = true;
      }      
    }
  });
  
  // 如果需要响应式更新
  cachedData.value = [...cachedData.value];
};

const currentEditRow = ref(null);

const openEditDialog = (row) => {
  currentEditRow.value = row;  
  
  editForm.value = row;  
  editForm.value.setValue = editForm.value.setValue ?? (editForm.value.defaultValue ?? editForm.value.value);

  editDialogVisible.value = true;

};

//查询群组信息参数
const queryVarGroupParams = reactive({
    deviceId:0, 
    slaveId:0,  
       name:''
})

const confirmEdit = async () => {
 
    // Update the row data
    Object.assign(currentEditRow.value, editForm.value); 
    if(!isConnected)
    {
      ElMessage.error(t('message.realTimeData.connectError'));
      return
    }  
    try {
          const token = getToken();                         
          // 1. 构造请求数据
          const requestData = [
              {
                  token: token,
                  deviceCode: currentDeviceInfo.value.deviceCode,
                  imei: undefined,
                  type: 4,  // 1=定时数据
                  dir: "Down",
                  timer: new Date().toISOString(),
                  priority: 1,
                  r_data: [],
                  w_data: []
              }
          ];         
          
          //2.追加点名数据
          requestData[0].r_data.push({ name: currentEditRow.value.varName });                        
          requestData[0].w_data.push({name: currentEditRow.value.varName,value:editForm.value.setValue / editForm.value.mult})
          
          await rollCall(requestData);
          editDialogVisible.value = false;
    }catch(error)
    {
          ElMessage.error(t('message.realTimeData.dataProcessError'));
    }  
 
};


// 加载设备树
const loadDeviceTree = async () => {
  try {
    isLoading.value = true;
    const res = await getCompanyDeviceTree({ companyId: null });
    
    if (res.success) {
      deviceTreeOptions.value = formatDeviceTree(res.data.list || []);
      //如果是跳转过来的则加载数据
      if (route.query.deviceId) {
        loadDeviceFromParams()
      }
    } else {
      ElMessage.error(res.msg || t('message.realTimeData.loadDeviceTreeFailed'));
    }
  } catch (error) {
    ElMessage.error(t('message.realTimeData.loadDeviceTreeFailed'));
  } finally {
    isLoading.value = false;
  }
};

// 格式化设备树数据
const formatDeviceTree = (treeData) => {
  return treeData.map(item => ({
    id: item.id,
    name: item.name,
    type: item.type,
    parentId: item.parentId,
    deviceCode: item.data?.device_code || '',
    children: item.children ? formatDeviceTree(item.children) : []
  }));
};

// 存储当前选中的节点数据
const currentSelectedNode = ref(null);

// 节点点击事件 - 获取完整节点数据
// const handleNodeClick = (nodeData) => {  
//   //ElMessage.error(`${nodeData} --${nodeData.id}--${nodeData.type}-1- handleNodeClick`);
//   currentSelectedNode.value = nodeData;
//   // 这里可以立即处理节点数据，或者等待 @change 事件
// };



/* // 改进的自定义指令（确保事件正确传递）
const vPreventClose = {
  mounted(el) {
    el.addEventListener('click', (e) => {
      // 只阻止会导致下拉框关闭的冒泡，不影响节点自身事件
      if (e.target.closest('.tree-node-content')) {
        e.stopPropagation();
      }
    }, true);
  }
}; */

// 节点内容点击处理
/* 
const handleNodeContentClick = (nodeData, event) => {
  
  
  console.log('点击节点内容:', nodeData);
  currentSelectedNode.value = nodeData;

  // 类型2：设备节点 - 触发选中
  if (nodeData.type === 2) {
    currentDeviceId.value = nodeData.id;
    handleDeviceIdChange(nodeData.id);
    treeSelectRef.value.blur();    
    // 设备节点选中后允许下拉框关闭
    return;
  }
// 阻止事件冒泡，避免触发 el-tree-select 的默认收起行为
  event.stopPropagation();
  // 类型非2：组织/文件夹节点
  if (nodeData.children && nodeData.children.length > 0) {
    // 有子节点：强制展开，保持下拉框打开
    forceExpandNode(nodeData.id);
  } else {
    // 没有子节点：提示只能选择设备
    ElMessage.warning(t('message.realTimeData.selectDeviceOnly'));
  }
};

// 强制展开节点
const forceExpandNode = (nodeId) => {
  if (!treeSelectRef.value) return;
  
  try {
    // 获取内部 tree 实例
    const treeInstance = treeSelectRef.value;
    
    // 使用 store 的 setExpanded 方法强制展开
    if (treeInstance.store && treeInstance.store.setExpanded) {
      ElMessage.warning(t('1--forceExpandNode'));
      treeInstance.store.setExpanded(nodeId, true);
    }
    
    console.log('强制展开节点:', nodeId);
    
  } catch (error) {
    console.error('展开节点失败:', error);
  }
};

// 节点点击事件处理
const handleTreeNodeClick = (nodeData) => {
    
  // 如果是非设备节点，阻止下拉框关闭
  if (nodeData.type !== 2) {
    // 阻止默认行为，保持下拉框打开
    return false;
  }
   
};


// 节点点击事件处理
const handleTreeNodeClick = (nodeData, node) => {
  console.log('点击节点:', nodeData);
  currentSelectedNode.value = nodeData;

  // 类型2：设备节点 - 触发选中
  if (nodeData.type === 2) {
    currentDeviceId.value = nodeData.id;
    handleDeviceIdChange(nodeData.id);
    return;
  }

  // 类型非2：组织/文件夹节点
  if (nodeData.children && nodeData.children.length > 0) {          
    // 直接修改节点的展开状态（关键修改）
    node.expanded = !node.expanded;
  } else {
    // 没有子节点：提示只能选择设备
    ElMessage.warning(t('message.realTimeData.selectDeviceOnly'));
  }
};
 

const handleTreeNodeClick = (nodeData) => {
  console.log('点击节点:', nodeData);
  
  // 存储节点数据
  currentSelectedNode.value = nodeData;
  
  // 类型2：设备节点 - 直接选中
  if (nodeData.type === 2) {
    currentDeviceId.value = nodeData.id;
    handleDeviceIdChange(nodeData.id);
    return;
  }
  
  // 类型1或其他：组织/文件夹节点
  if (!nodeData.children || nodeData.children.length === 0) {
    // 没有子节点：提示选择设备
    ElMessage.warning(t('message.realTimeData.selectDeviceOnly'));
  }
  // 有子节点的非设备节点，让 el-tree-select 处理默认的展开行为
};

// 树节点点击事件处理
const handleTreeNodeClick = (nodeData) => {
  // 阻止事件冒泡，避免触发默认行为
  //event?.stopPropagation();
  
  console.log('点击节点:', nodeData);
  
  // 存储节点数据
  currentSelectedNode.value = nodeData;
  
  // 类型2：设备节点 - 直接选中
  if (nodeData.type === 2) {
    currentDeviceId.value = nodeData.id;
    handleDeviceIdChange(nodeData.id);
   // ElMessage.success(`已选择设备: ${nodeData.name}`);
    return;
  }
  
  // 类型1或其他：组织/文件夹节点
  if (nodeData.children && nodeData.children.length > 0) {
    // 有子节点：手动切换展开状态
    // 由于 el-tree-select 没有直接的方法，我们通过修改数据来模拟展开
    //toggleNodeExpansion(nodeData);
  } else {
    // 没有子节点：提示选择设备
    ElMessage.warning(t('message.realTimeData.selectDeviceOnly'));
  }
};

// 切换节点展开状态
const toggleNodeExpansion = (nodeData) => {
  // 由于 el-tree-select 没有提供直接控制展开的API，
  // 我们可以通过修改数据来触发重新渲染，或者使用其他方式
  console.log('切换节点展开状态:', nodeData.name);
        //ElMessage.error(`${nodeData.name} toggleNodeExpansion`);        

       // nodeData.expanded = !nodeData.expanded;
  // 这里可以添加自定义的展开状态管理
  // 由于技术限制，可能需要使用其他方案
};

// 树节点点击事件处理
const handleTreeNodeClick = (nodeData, treeNode) => {
  // 存储当前点击的节点数据
  currentSelectedNode.value = nodeData;
  
  // 如果节点类型是2（设备），则选中该节点
  if (nodeData.type === 2) {
    currentDeviceId.value = nodeData.id;
    // 触发设备变更处理
    handleDeviceIdChange(nodeData.id);
  } else {
    // 如果不是设备节点，且有子节点，则展开/折叠
    if (nodeData.children && nodeData.children.length > 0) {
      // 切换节点的展开状态
      treeNode.expanded = !treeNode.expanded;
    } else {
      // 如果没有子节点，显示提示信息
      ElMessage.warning(t('message.realTimeData.selectDeviceOnly'));
    }
  }
};
 */
// 设备变更事件
const handleDeviceChange = (device) => {
  if(device==null) return;
  //ElMessage.error(`${device.deviceCode} handleDeviceChange`);
  handleDeviceIdChange(device.id);
  console.log('设备变更:', device)
}


const handleNodeClick = (nodeData,node) => {
  currentSelectedNode.value = nodeData;
  console.log('节点点击:', node)
}

// ID变化事件
const handleDeviceIdChange = async (selectedId) => {    

    if (!selectedId) {
      currentDeviceId.value = null;
      currentSelectedNode.value = null;
      tableData.value = [];
      cachedData.value = [];
      return;
    }  
    
    //ElMessage.success(`handleDeviceIdChange: ${selectedId}`)
    // 使用存储的节点数据
    if (currentSelectedNode.value && currentSelectedNode.value.type === 2) {
      const nodeData = currentSelectedNode.value;
      currentDeviceId.value = nodeData.id;     
      await loadSlave(nodeData.id);
        
      const params = {
        companyId: nodeData.parentId,
        name: undefined,
        deviceCode: nodeData.deviceCode,
        pageNum: 1,
        pageSize: 10
      }

      const deviceDeails = await getDevices(params);
      if(deviceDeails.success && deviceDeails.data.list.length>0) {
        currentDeviceInfo.value = {
            parentId: nodeData.parentId,
            name: nodeData.name,
            deviceCode: nodeData.deviceCode,
            organization: deviceDeails.data.list[0].company_name,
            template: deviceDeails.data.list[0].template_name,
            gateway: deviceDeails.data.list[0].gateway_name,
            address: deviceDeails.data.list[0].location,
            description: deviceDeails.data.list[0].description
        };
      } else {
        currentDeviceInfo.value = {
            parentId: nodeData.parentId,
            name: nodeData.name,
            deviceCode: nodeData.deviceCode,
            organization: '',
            template: '',
            gateway: '',
            address:'',
            description: '0'
        };
      }
    } else {
      currentDeviceId.value = null;
      tableData.value = [];
      cachedData.value = [];
        currentDeviceInfo.value = {
            parentId: 0,
            name: '',
            deviceCode: '',
            organization: '',
            template: '',
            gateway: '',
            address:'',
            description: ''
        };
      //ElMessage.warning(t('message.realTimeData.selectDeviceOnly'));
    }
};


const handleDeviceByRotue =async (selectedId) => {    
    if (!selectedId) {
      currentDeviceId.value = null; // 明确设置为null
      tableData.value = [];
      cachedData.value = [];
      return;
    }  
    

    //ElMessage.error(`${selectedId}-1- handleDeviceByRotue`);

    currentDeviceId.value = 0;

    tableData.value = [];
    cachedData.value = [];
    // 查找选中的节点
    const findNode = (nodes, id) => {    
      for (const node of nodes) {      
       // ElMessage.error(`id:${id}--nodeid:${node.id}--type:${node.type}-name:${node.name}-1-1- handleDeviceByRotue`);
        //if (node.id === id && node.type === 2) 
         if (Number(node.id) === Number(id) && Number(node.type) === 2) 
        {        
        //   ElMessage.error(`id:${id}--nodeid:${node.id}--type:${node.type}-name:${node.name}-1-1- handleDeviceByRotue`);
         // ElMessage.error(`${node.id}-1-1- handleDeviceByRotue*************`);
          return node;
        }

           // 递归查找子节点
        if (node.children && node.children.length > 0) {
          const found = findNode(node.children, id);
          if (found) {
            return found; // 关键：找到后立即返回
          }
        }
      }
      return null;
    };     
    
    const selectedNode = findNode(deviceTreeOptions.value, selectedId);
    
    if (!selectedNode) {
     // ElMessage.error(`${selectedId}-2- handleDeviceByRotue`);
      currentDeviceId.value = null; // 未找到节点时清空
      return;
    }
    
    //ElMessage.error(`${selectedId}-3- handleDeviceByRotue`);

    // 检查设备类型（type=2表示设备）
    if (selectedNode.type === 2) {

      //ElMessage.error(`${selectedId}-4- handleDeviceByRotue`);

      currentDeviceId.value = selectedNode.id;     
      await  loadSlave(selectedNode.id);
        
      const params = {
        companyId: selectedNode.parentId,
        name: undefined,
        deviceCode: selectedNode.deviceCode,
        pageNum: 1,
        pageSize: 10
      }

      const deviceDeails = await getDevices(params);
      if(deviceDeails.success && deviceDeails.data.list.length>0)
      {
        currentDeviceInfo.value = {
            parentId: selectedNode.parentId,
            name: selectedNode.name,
            deviceCode: selectedNode.deviceCode,
            // 其他信息可以从设备数据中获取或通过API查询
            organization: deviceDeails.data.list[0].company_name,
            template: deviceDeails.data.list[0].template_name,
            gateway: deviceDeails.data.list[0].gateway_name,
            address:deviceDeails.data.list[0].location,
            description: deviceDeails.data.list[0].description
          };          
      }
      else
      {

        currentDeviceInfo.value = {
            parentId: selectedNode.parentId,
            name: selectedNode.name,
            deviceCode: selectedNode.deviceCode,
            // 其他信息可以从设备数据中获取或通过API查询
            organization: '',
            template: '',
            gateway: '',
            address:'',
            description: '0'
          };
      }
      //查询数据
      await handleQuery();
    }
    else {
      // 当选择的不是设备节点时，清空选择
      currentDeviceId.value = null;
      tableData.value = [];
      cachedData.value = [];
    }
};   


// 加载从机方法
const loadSlave = async (deviceId) => {
  try {
    if (!deviceId) {
      ElMessage.warning(t('message.varableSelectorControl.selectDeviceFirst'));
      return;
    }
    
    const res = await slavedetailbydevice({
      deviceId: deviceId,
      pageNum: 1,
      pageSize: 1000
    });
    //变量组参数
    queryVarGroupParams.deviceId = deviceId;
    
    if (res.success) {       
      const fetchedSlaves = res.data.list || [];
      
      // 转换数据格式，确保有 value 和 label 属性
      slaveOptions.value = [
        {
          value: -1,
          label: t('message.varableSelectorControl.allSlaves')
        },
        ...fetchedSlaves.map(slave => ({
          value: slave.slave_id, // 确保使用正确的ID字段
          label: slave.slave_name || `Slave ${slave.slave_id}`
        }))
      ];
      
      // 默认选中第一个从机（"全部从机"选项）
      if (slaveOptions.value.length > 0) {
        currentSlaveId.value = slaveOptions.value[0].value;
        queryVarGroupParams.slaveId = currentSlaveId.value>0 ? currentSlaveId.value : undefined;
        //加载分组信息
        await loadVariableGroups(queryVarGroupParams.deviceId,queryVarGroupParams.slaveId);
      }
    } else {
      ElMessage.error(res.msg || t('message.varableSelectorControl.loadSlaveFailed'));
    }
  } catch (error) {    
    ElMessage.error(t('message.varableSelectorControl.loadSlaveFailed'));
    console.error('加载从机失败:', error);
  }
};



// 添加从机变更处理
const handleSlaveChange =async (slaveId) => {
  
   try {

    queryVarGroupParams.slaveId = slaveId>0 ? slaveId : undefined;
    //加载分组信息
    await loadVariableGroups(queryVarGroupParams.deviceId,queryVarGroupParams.slaveId);

     
  } catch (error) {    
    ElMessage.error(t('message.varableSelectorControl.loadSlaveFailed'));
    console.error('加载从机失败:', error);
  }
  // 这里可以添加从机变更后的逻辑
};

// 修改处理函数
const handleGroupChange = async (groupIds) => {
  try {
    // 获取选中的分组名称
    selectedGroupNames.value = groupIds.map(groupId => {
      const selectedOption = VarGroupOptions.value.find(option => option.value === groupId);
      return selectedOption ? selectedOption.label : '';
    }).filter(name => name);

    console.log('选中的分组IDs:', groupIds);
    console.log('选中的分组名称:', selectedGroupNames.value);
    
    // 重新查询数据
    await handleQuery();
  } catch (error) {
    
    console.error('处理分组变更失败:', error);
  }
};


// 修改加载分组函数，确保包含"全部分组"选项
const loadVariableGroups = async (deviceId, slaveId) => {
  try {
    if (!deviceId) return;
    
    const res = await vargroupdetailbydevice({
      deviceId: deviceId,
      slaveId: slaveId || undefined,
      pageNum: 1,
      pageSize: 1000
    });
    
    if (res.success) {
      VarGroupOptions.value = [
        { value: -1, label: t('message.realTimeData.allGroups') },
        ...res.data.list.map(group => ({
          value: group.id,
          label: group.name_group
        }))
      ];

      // 默认选中"全部分组"
      if (VarGroupOptions.value.length > 0) {
        selectedGroupIds.value = [-1];
        selectedGroupNames.value = [t('message.realTimeData.allGroups')];
      }
    }    
  } catch (error) {
    console.error('加载变量分组失败:', error);
  }
};

// 在 script setup 中添加排序状态
const sortInfo = ref({
  prop: 'varName', // 默认排序字段
  order: 'descending' // 默认排序方式
});

// 表格排序变化处理
/* const handleSortChange = ({ prop, order }) => {
  sortInfo.value = { prop, order };
  incrementalUpdateCache(cachedData.value); // 重新应用排序
}; */

// 更新表格
const updateTable = (data) => {
  

  const transformedData = data.map(item => (
    {    
    deviceCode:item.device_code ?? '--',
    deviceName: item.device_name ?? '--',
    varName: item.var_name ?? '--',
    display_name: item.display_name ?? '--',
    name_group: item.name_group ?? '--',
    description: item.description ?? '--',
    slaveName: item.slave_name ?? '--',
    varType: item.var_type ?? '--',
    updateTime: item.update_at ? item.update_at.replace('T', ' ') : '--',
    setValue: item.set_value ?? '--' ,  
    currentValue: (item.current_value === null ||  item.error_code>0) ?  '--' : item.current_value,
    rwTypeId:item.rw_type_id,
    mult:item.mult,
    minimum:item.minimum,
    maximum:item.maximum,
    var_decimal:item.var_decimal,
    default_value:item.default_value,        
    unit:item.unit
  }));
  
  tableData.value =transformedData;   
  cachedData.value = transformedData;
  
};

const incrementalUpdateCache = (newData) => {
  const dataMap = new Map(cachedData.value.map(item => [item.deviceCode + item.varName, item]));


  newData.forEach(item => {
    const varName = item.varName || '--';
    const deviceCode = item.deviceCode;
    const key = deviceCode + varName;
    const newTimestamp = getValidTimestamp(item.updateTime);

    // 获取现有数据（避免重复查找）
    const existingItem = dataMap.get(key);

    // 优化后的判断条件
    if ((newTimestamp && newTimestamp > getValidTimestamp(existingItem.updateTime))) {
      dataMap.set(key, {
        ...(existingItem || {}), // 保留已有属性
        varName: varName,
        updateTime: item.updateTime,
        currentValue: item.currentValue ?? '--', // 使用nullish coalescing
        timestamp: newTimestamp || Date.now()
      });
    }
  });

  // 辅助函数：安全获取时间戳
  function getValidTimestamp(time) {
    try {
      return time ? new Date(time).getTime() : null;
    } catch {
      return null;
    }
  }

    // 辅助函数：根据当前排序规则比较
  const compareFn = (a, b) => {
    const { prop, order } = sortInfo.value;
    const aValue = a[prop] || '';
    const bValue = b[prop] || '';
    
    // 处理时间字段特殊比较
    if (prop === 'varName') {
      const aTime = new Date(aValue).getTime();
      const bTime = new Date(bValue).getTime();
      return order === 'ascending' ? aTime - bTime : bTime - aTime;
    }
    
    // 默认字符串比较
    if (order === 'ascending') {
      return String(aValue).localeCompare(String(bValue));
    } else {
      return String(bValue).localeCompare(String(aValue));
    }
  };

  const updatedData = Array.from(dataMap.values()).sort(compareFn);  
  
  tableData.value = updatedData;  
  cachedData.value = updatedData;
};



const processDeviceData = (message) => {
  try {
    const receivedData = JSON.parse(message);
    const receivedTime = new Date(receivedData.time);        
    const newData = receivedData.Vars?.map(item => ({
      deviceCode: receivedData.DeviceCode,
      varName: item.Name,
      error_code: item.Error,
      updateTime: receivedTime.toLocaleString(),
      currentValue: (item.Value === null ||  item.Error>0) ?  '--' : item.Value
    })) || [];
    
    incrementalUpdateCache(newData);
    return cachedData.value;
  } catch (error) {
    console.error('数据处理错误:', error);
    return [];
  }
};
 


// 发送订阅请求
const sendSubscriptionRequest = async () => {
  if (!isConnected.value || !cachedData.value.length) return;
  if(!error) 
  {
        ElMessage.error(`${error}`);
  }

  try {
    const request = {
      token: getToken(),
      deviceCode: currentDeviceInfo.value.deviceCode,
      imei: null,
      Dir:"d",  //下行数据
      type: 1, // 实时数据订阅
      timer:new Date().toISOString(),
      priority:1,
      r_data: []
    };

      cachedData.value.forEach(item => {              
            if (!request.r_data.some(existing => existing.name === item.varName)) {
                request.r_data.push({ name: item.varName });
            }
        });

    await invoke('RequestData', JSON.stringify(request))
  } catch (error) {
    console.error('订阅失败:', error);
  }
};
 
// 点名操作
const handleQuery = async () => {
    
  if (!isConnected.value) {    
    ElMessage.error(t('message.realTimeData.connectError'));
    return;
  }
  
    // 重置滚动相关状态
  hasMoreData.value = true;
  //pagination.currentPage = 1;
  
  try { 
        let nameGroups = undefined;
        if (selectedGroupIds.value.length > 0) {
          // 排除"全部分组"选项(-1)，只传递实际选中的分组
          const validGroupIds = selectedGroupIds.value.filter(id => id !== -1);
          if (validGroupIds.length > 0) {
            // 根据ID获取对应的分组名称
            nameGroups = validGroupIds.map(groupId => {
              const group = VarGroupOptions.value.find(g => g.value === groupId);
              return group ? group.label : null;
            }).filter(name => name);
          }
        }

      if(currentDeviceInfo.value.deviceCode=="")
      {
          return;
      }
      const parms = [{
        lang:localStorage.getItem('lang') !=null ? localStorage.getItem('lang') : 'zh-CN' ,
        company_id: currentDeviceInfo.value.parentId,
        device_code: currentDeviceInfo.value.deviceCode,                
        slave_id: currentSlaveId.value>0 ?  currentSlaveId.value : undefined, // 更简洁的处理方式
        names:  undefined,
        name_group:nameGroups && nameGroups.length > 0 ? nameGroups : undefined,// (selectedGroupIds.value>0 && selectedGroupNames.value) ? selectedGroupNames.value : undefined,
        display_names: currentVarName.value ? [currentVarName.value] : undefined,
        pageNum: pagination.currentPage,
        pageSize: pagination.pageSize
      }];
      
      const response= await getRealtimeList(parms)
      if(response.success)
      {
          pagination.total = response.data.totalCount
          updateTable(response.data.list)
          //表格更新后，订阅
          sendSubscriptionRequest();

          // 检查是否还有更多数据
          if (pagination.currentPage * pagination.pageSize >= pagination.total) {
            hasMoreData.value = false;
          }          
      }
      else
      {
        ElMessage.error(response?.msg);
      }
  } catch (error) {
    ElMessage.error(t('message.realTimeData.dataProcessError'));
  }
};

// 主动采集数据
const handleActiveCollection =async (row) => {
  if(!isConnected)
  {
    ElMessage.error(t('message.realTimeData.connectError'));
    return
  }  
  try {
        const token = getToken();                         
        // 1. 构造请求数据
        const requestData = [
            {
                lang :  localStorage.getItem('lang') !=null ? localStorage.getItem('lang') : 'zh-CN' ,
                token: token,
                deviceCode: currentDeviceInfo.value.deviceCode,
                imei: undefined,
                type: 4,  // 1=定时数据
                dir: "Down",
                timer: new Date().toISOString(),
                priority: 1,
                r_data: [],
                w_data: []
            }
        ];         

        //2.追加点名数据
        requestData[0].r_data.push({ name: row.varName });                        
        await rollCall(requestData);
  }catch(error)
  {
        ElMessage.error(t('message.reaTimePlot.dataProcessError'));
  }  
};

// 分页处理
const handlePageChange = async() =>
{ 
   await handleQuery();
}

const handleSizeChange = async(size) => {
  pagination.pageSize = size;
  pagination.currentPage = 1;
  await handleQuery();
};

// 初始化自动刷新
const initAutoRefresh = () => {
  stopAutoRefresh();
  
  // 首次订阅
  if (cachedData.value.length > 0) {
    sendSubscriptionRequest();
  }
  
  // 设置定时刷新
  refreshTimer.value = setInterval(() => {
    if (cachedData.value.length > 0) {
      sendSubscriptionRequest();
    }
  }, refreshInterval);
  
  // 设置心跳
  heartBeatTimer.value = setInterval(() => {
    heart();
    // 心跳逻辑
  }, heartBeatInterval);
};

// 停止自动刷新
const stopAutoRefresh = () => {
  if (refreshTimer.value) {
    clearInterval(refreshTimer.value);
    refreshTimer.value = null;
  }
  
  if (heartBeatTimer.value) {
    clearInterval(heartBeatTimer.value);
    heartBeatTimer.value = null;
  }
};


// 自定义消息处理函数
const handleNotification =async (message) => {

  try {    
    // 1. 处理并获取最新数据    
    processDeviceData(message);        
  } catch (error) {
    console.error('处理数据错误:', error);
    ElMessage.error(t('message.realTimeData.dataProcessError'));
  }

}


// 改进滚动容器查找逻辑
const bindScrollListener = () => {
  removeScrollListener();
  
  if (!tableRef.value?.$el) {
    console.warn('表格元素未找到');
    return;
  }

  // 尝试多种可能的选择器
  const possibleSelectors = [
    '.el-table__body-wrapper',
    '.el-scrollbar__wrap',
    '[class*="scrollbar"]',
    '[class*="body-wrapper"]'
  ];

  for (const selector of possibleSelectors) {
    const elements = tableRef.value.$el.querySelectorAll(selector);
    if (elements.length > 0) {
      // 找到第一个可滚动的元素
      for (const element of elements) {
        if (element.scrollHeight > element.clientHeight) {
          scrollContainer = element;
          break;
        }
      }

      if (scrollContainer) break;
    }
  }

  if (scrollContainer) {
    scrollContainer.addEventListener('scroll', handleScroll);

    console.log('滚动监听已绑定到容器:', scrollContainer.className);

    // 添加样式以便调试
    //scrollContainer.style.border = '2px solid red';
  } else {

    console.warn('未找到滚动容器，检查表格选择器:', tableRef.value.$el.innerHTML);
    
    // 尝试直接使用表格的body-wrapper
    const bodyWrapper = tableRef.value.$el.querySelector('.el-table__body');
    if (bodyWrapper && bodyWrapper.parentNode) {
      scrollContainer = bodyWrapper.parentNode;
      scrollContainer.addEventListener('scroll', handleScroll);
      console.log('使用备用方案绑定滚动监听');
    }
  }
};
 

// 移除滚动监听
const removeScrollListener = () => {
  if (scrollContainer) {
    scrollContainer.removeEventListener('scroll', handleScroll);
  }  
  //clearTimeout(debounceTimer);
  scrollContainer = null;  
};


// 添加边界状态监控
const checkBoundaryAndTrigger = () => {
  if (!scrollContainer) return;
  
  const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
  const distanceToBottom = scrollHeight - scrollTop - clientHeight;
  const distanceToTop = scrollTop;
  
  // 如果已经在边界，手动触发加载
  const isAtBottom = distanceToBottom <= 0;
  const isAtTop = distanceToTop <= 0;
  
  if (isAtBottom && hasMoreData.value && !isLoadingMore.value) {
    console.log('检测到已到达底部，触发加载');
    loadMoreData(1);
  } else if (isAtTop && pagination.currentPage > 1 && !isLoadingMore.value) {
    console.log('检测到已到达顶部，触发加载');
    loadMoreData(-1);
  }
};


// 滚动处理函数
const handleScroll = () => {
  //ElMessage.error(`-1---handleScroll`);

    // 如果滚动被锁定，直接返回
  if (isScrollLocked.value) {
    return;
  }

  clearTimeout(debounceTimer);

    
  // 每次滚动都检查边界
  checkBoundaryAndTrigger();
  
  debounceTimer = setTimeout(() => {
    
   // ElMessage.error(`-2-scrollContainer:${!scrollContainer}--isLoadingMore.value:${isLoadingMore.value }--!hasMoreData.value :${!hasMoreData.value }--`);
    if (!scrollContainer || isLoadingMore.value || !hasMoreData.value) {
      return;
    }
    
    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    const distanceToBottom = scrollHeight - scrollTop - clientHeight;
    const distanceToTop = scrollTop;
  //  ElMessage.error(`-3-distanceToBottom:${distanceToBottom}--- handleScroll`);
   // ElMessage.error(`-3-distanceToBottom:${distanceToBottom}--isScrolling:${scrollHeight}--scrollTop:${scrollTop}--clientHeight:${clientHeight}- handleScroll`);
    // 滚动到底部50px范围内时加载更多
     if (distanceToBottom <= 50 && distanceToBottom >= 0)  {    
    //  ElMessage.error(`-4-distanceToBottom:${distanceToBottom}--- handleScroll`);
      loadMoreData(1);
    }
        // 滚动到顶部50px范围内时加载上一页
    else if (distanceToTop <= 5 && distanceToTop >= 0 && pagination.currentPage > 1) {
    //  ElMessage.error(`-5-触发加载上一页，距离顶部:${distanceToTop}`);
      loadMoreData(-1);
    }
  }, 300);
};

// 加载更多数据
const loadMoreData = async(pagNum) => {

  //ElMessage.error(`-1-isLoadingMore.value:${isLoadingMore.value}--!hasMoreData.value:${!hasMoreData.value}- loadMoreData`);
  //ElMessage.error('1-loadMoreData');    
  if (isLoadingMore.value || !hasMoreData.value) {
    return;
  }
  
  //ElMessage.error('2-loadMoreData');    
  
  isLoadingMore.value = true;
  try {
    const nextPage = pagination.currentPage + pagNum;
   // ElMessage.error(`-1-currentPage:${pagination.currentPage}--nextPage:${nextPage}- loadMoreData`);

    const parms = [{
      lang: localStorage.getItem('lang') !=null ? localStorage.getItem('lang') : 'zh-CN' ,
      company_id: currentDeviceInfo.value.parentId,
      device_code: currentDeviceInfo.value.deviceCode,                
      slave_id: currentSlaveId.value > 0 ? currentSlaveId.value : undefined,
      names: undefined,
      name_group: selectedGroupIds.value.length > 0 ? 
        selectedGroupIds.value.filter(id => id !== -1)
          .map(groupId => {
            const group = VarGroupOptions.value.find(g => g.value === groupId);
            return group ? group.label : null;
          })
          .filter(name => name) : 
        undefined,
      display_names: currentVarName.value ? [currentVarName.value] : undefined,
      pageNum: nextPage,
      pageSize: pagination.pageSize
    }];
    
    const response = await getRealtimeList(parms);
    if (response.success)
    {
      if (response.data.list.length > 0) {


          pagination.total = response.data.totalCount
          updateTable(response.data.list)
          //表格更新后，订阅
          sendSubscriptionRequest();

        
        pagination.currentPage = nextPage;
        pagination.total = response.data.totalCount;        
        // 检查是否还有更多数据
        hasMoreData.value = pagination.currentPage * pagination.pageSize < pagination.total;
        /* if (tableData.value.length >= pagination.total) {
          hasMoreData.value = false;
        } */
      } else {
        //ElMessage.error('4-loadMoreData');    
        hasMoreData.value = false;
      }
    }
  } catch (error) {
    console.error('加载更多数据失败:', error);
    ElMessage.error(t('message.realTimeData.loadMoreError'));
  } finally {
    isLoadingMore.value = false;
  }
};

const RealeaseSubscribe = async () => {
  if (!isConnected) return true; // 返回true表示无需处理

  try {
    const token = getToken();
    if (!token || !currentDeviceInfo.value.deviceCode) {
      return true; // 没有需要释放的资源
    }
    
    const params = {
      token: token,
      deviceCode: currentDeviceInfo.value.deviceCode,
      imei: null,
      Dir: "d",
      type: 1,
      priority: 1,
      timer: new Date().toISOString(),
      r_data: [],
    };

    const jsonString = JSON.stringify(params);

    await invoke("ReleaseData", jsonString);

    return true; // 表示成功
  } catch (err) {
    console.error("释放订阅失败:", err);
    //ElMessage.error(t("message.reaTimePlot.releaseFailed"));
    return false; // 表示失败
  }
};


// 页面离开处理
onBeforeRouteLeave(async () => {
  if (cachedData.value.length === 0) {    
    return;
  }
  
  try {    
    stopAutoRefresh();
    await RealeaseSubscribe();
  } catch (error) {
    if (error !== 'cancel') {
      console.error('离开页面错误:', error);
    }    
  }
});


// 从路由参数加载设备信息
const loadDeviceFromParams = () => {
  const { deviceId, deviceName } = route.query
  
  if (deviceId) {
    currentDeviceId.value = deviceId
    // 解码设备名称（因为传递时使用了 encodeURIComponent）
    currentDeviceInfo.value.name = deviceName ? decodeURIComponent(deviceName) : ''
    
    // 这里可以根据 deviceId 调用 API 获取更详细的设备信息
    loadDeviceDetails(deviceId)
    
    //ElMessage.success(`已加载设备: ${currentDeviceInfo.value.name}-${deviceId}`)
  }
}

// 根据设备ID 选定设备
const loadDeviceDetails = async (deviceId) => {
  try {

  //  ElMessage.success(`loadDeviceDetails: ${deviceId}`)
    //选定设备
    handleDeviceByRotue(deviceId);
 
    // 临时示例 - 你可以根据实际情况调用API
    console.log('加载设备详情:', deviceId)
  } catch (error) {
    ElMessage.error('加载设备详情失败')
  }
}


// 监听路由参数变化
watch(
  () => route.query,
  (newQuery) => {
    if (newQuery.deviceId || newQuery.deviceName) {
      loadDeviceFromParams()
    }
  }
)

// 只监听数组引用变化，不监听内部对象变化
 watch(
  () => tableData.value, 
  (newVal, oldVal) => {
    // 只有当整个数组被替换时才重新绑定
    if (newVal !== oldVal) {
      nextTick(() => {
        bindScrollListener();
      });
    }
  },
  { deep: false } // 明确设置为非深度监听
);

// 组件生命周期
onMounted(async () => {  
  await loadConfig();
  loadColumnSettings();
  
  await start()  ;
  // 注册消息处理器
  on('ReceiveMessage', handleNotification);
  
  initAutoRefresh();

    // 延迟绑定滚动监听，确保表格已渲染
  setTimeout(() => {
    bindScrollListener();
  }, 500);
  //该方法要在最后
  await loadDeviceTree();

});

onBeforeUnmount(() => {
  stopAutoRefresh();
  if (chartInstance.value) {
    chartInstance.value.dispose();
  }
  off('ReceiveMessage', handleNotification);
  stop();
  
  removeScrollListener();
  clearTimeout(debounceTimer);
});


// 组件卸载时清理
onUnmounted(async  () => {   

    if (trackTimer) 
    {   
        clearInterval(trackTimer);
        trackTimer = null;
    }

  
  try {
    unsubscribe();
    off('ReceiveMessage', handleNotification)
    stop()
  }
  catch(error)
  {
      console.error('fail:', error)
  }
})


// 注册连接成功回调
const unsubscribe = onConnected(() => {  
  sendSubscriptionRequest();
  console.log('连接成功回调执行')
  // 在这里执行连接成功后需要做的操作
  // 比如初始化数据、订阅消息等
})

</script>
<style scoped>
.real-time-monitor {
  padding: 20px;
  height: 95%; /* 改为vh单位 */
  overflow: hidden;
  background-color: #f5f7fa;
}

.connection-alert {
  margin-bottom: 16px;
}

/* 布局容器 - 上下结构 */
.layout-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: calc(100vh - 120px); /* 调整高度计算 */
}

/* 上方设备信息面板 */
.top-panel {
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  overflow: hidden;
  min-height: 50px;
  max-height: 30vh;
}

.top-panel.collapsed {
  max-height: 50px;
}

/* 设备信息头部 */
.device-info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f5f7fa;
  border-bottom: 1px solid #e4e7ed;
  cursor: pointer;
  user-select: none;
  transition: all 0.3s;
}

.device-info-header:hover {
  background: #ebedf0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.header-left .el-icon {
  transition: transform 0.3s;
  flex-shrink: 0;
}

.header-left .el-icon.rotate {
  transform: rotate(180deg); /* 向上折叠时箭头朝上 */
}

.header-title {
  font-weight: 600;
  color: #303133;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-right {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

/* 设备信息内容 */
.device-info-content {
  padding: 0;
  max-height: calc(100% - 70px);
  overflow-y: auto;
}

.top-panel.collapsed .device-info-content {
  display: none;
}

/* 下方控制面板 */
.bottom-panel {
  flex: 1;
  min-height: 0;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}

.bottom-panel.expanded {
  /* 当设备信息折叠时，控制面板占据更多空间 */
  height: auto;
}

/* 控制面板 */
.control-panel {
  background: #fff;
  padding: 16px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  height: 97%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 筛选表单 */
.filter-form {
  flex-shrink: 0;
  margin-bottom: 0;
}

/* 数据表格容器 - 关键修改 */
.data-table-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  position: relative;
}

/* 表格包装器 - 新增 */
.table-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* 表格样式调整 */
.el-table {
  flex: 1;
  min-height: 200px; /* 确保表格有最小高度 */
}

/* 分页样式调整 - 关键修改 */

.pagination {
  margin-top: 16px;
  padding: 12px 0;
  background: #fff;
  border-top: 1px solid #ebeef5;
  display: flex;
  flex-shrink: 0;
}

/* 确保分页始终可见 */
.pagination-container {
  position: sticky;
  bottom: 0;
  background: white;
  z-index: 10;
  padding: 12px 0;
  border-top: 1px solid #ebeef5;
  justify-content: flex-end; /* 始终右对齐 */
}
/* 分页组件内部也右对齐 */
:deep(.el-pagination) {
  justify-content: flex-end !important;
}


/* 设备信息容器 */
.device-info-container {
  padding: 16px;
}

.device-header {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
}

.device-header h2 {
  margin: 0;
  font-size: 18px;
  color: #303133;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.device-id {
  margin-top: 4px;
  font-size: 14px;
  color: #909399;
}

/* 设备详情网格 */

.device-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 30px;
  font-size: 14px;
}

.device-details-grid .detail-item {
  display: flex;
  line-height: 1.6;
  min-height: 32px;
  align-items: flex-start;
}

.device-details-grid .detail-label {
  width: 120px;
  color: #606266;
  font-weight: 500;
  flex-shrink: 0;
}

.device-details-grid .detail-value {
  flex: 1;
  color: #303133;
  word-break: break-all;
} 


.detail-item {
  display: flex;
  line-height: 1.6;
  min-height: 32px;
  align-items: flex-start;
}

.detail-label {
  width: 120px;
  color: #606266;
  font-weight: 500;
  flex-shrink: 0;
}

.detail-value {
  flex: 1;
  color: #303133;
  word-break: break-word;
}

/* 表格高度计算 - 根据设备信息显示状态调整 */
:deep(.el-table) {
  height: v-bind(tableHeight) !important;
}

/* 响应式设计 */
@media (max-width: 992px) {
  .layout-container {
    gap: 12px;
  }
  
  .control-panel {
    padding: 12px;
  }
  
  .device-info-container {
    padding: 12px;
  }
  
  .detail-item {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .detail-label {
    width: auto;
    margin-bottom: 4px;
  }
}

@media (max-width: 768px) {
  .real-time-monitor {
    padding: 12px;
    height: 100vh;
  }
  
  .layout-container {
    gap: 12px;
    height: calc(100vh - 80px);
  }
  
  .pagination {
    padding: 8px 0;
  }
  
  :deep(.el-pagination) {
    justify-content: center !important;
  }
  
  :deep(.el-pagination__total),
  :deep(.el-pagination__sizes),
  :deep(.el-pagination__jump) {
    margin-bottom: 8px;
  }
}

/* 滚动条优化 */
.device-info-content::-webkit-scrollbar {
  width: 6px;
}

.device-info-content::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 3px;
}

.device-info-content::-webkit-scrollbar-thumb:hover {
  background: #909399;
}

/* 表格滚动条优化 */
:deep(.el-table__body-wrapper)::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

:deep(.el-table__body-wrapper)::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 4px;
}

:deep(.el-table__body-wrapper)::-webkit-scrollbar-thumb:hover {
  background: #909399;
}



.tree-node-content {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.device-icon {
  color: #409EFF;
  font-size: 16px;
}

.company-icon {
  color: #67C23A;
  font-size: 16px;
}

.default-icon {
  color: #909399;
  font-size: 16px;
}

.node-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.device-code {
  font-size: 12px;
  color: #909399;
  margin-left: 4px;
}

/* 确保下拉选项中的图标也正确显示 */
:deep(.el-select-dropdown__item) .tree-node-content {
  padding: 0;
}

/*设备控件*/

/* 统一控制元素样式 */
.filter-control {
  flex: 1;
  min-width: 180px;
}

/* 级联选择器高度调整 */
.filter-control:deep(.el-cascader .el-input__inner) {
  height: 32px;
  line-height: 32px;
}

.variable-selector:deep(.selector-header) {
  height: 32px;
  min-height: 32px;
  line-height: 32px;
  padding: 0 12px;
  display: flex;
  align-items: center;
}

/* 数据表格区域 */
.data-table {
  width: 100%;
}
/* 变量选择器下拉面板 */
.variable-selector:deep(.selector-dropdown) {
  top: 100% !important;
  width: 100%;
}

</style>